import type { ComputedProperties, ComputedPropertySchema, MatchableStringSchema } from './ReplicantComputedProperties';
import type { ComputedPropertyScriptInternal } from './ReplicantComputedPropertyScripts';
import type SB from './SchemaBuilder';
import type { FilterKeys } from './utils/TypeUtils';

export type NumberFilters = {
    between?: [number, number];
    lessThan?: number;
    greaterThan?: number;
    lessThanOrEqual?: number;
    greaterThanOrEqual?: number;
};

export type BooleanFilters = {
    is?: boolean;
    isNot?: boolean;
};

export type StringFilters<T extends string = string> = {
    isAnyOf?: T[];
    isNoneOf?: T[];
};

export interface StringExistsFilters<T extends string = string> extends StringFilters<T> {
    /** Select or omit results with at least one nested object property value that includes this field. */
    exists?: boolean;
}

export type MatchableStringFilters = {
    /**
     * A non-empty array of non-empty strings.
     *
     * Search terms ignore case and special Latin alphabet characters (e.g., `ä` is handled as `a`).
     */
    matchesAnyOf?: string[];
};

export type ArrayFilters<TSubType = any> = {
    containsAnyOf?: TSubType[];
    containsNoneOf?: TSubType[];
};

export type ArrayObjectFilters<T extends object> = {
    [K in keyof T]?: PrimitiveToFilter<T[K]>;
};

export type ObjectFilters<T extends object> = {
    [K in keyof T]?: SchemaToFilter<T[K]>;
};

// prettier-ignore
type SelectBuilder<T> =
    T extends MatchableStringSchema ? MatchableStringFilters :
    T extends SB.BooleanSchema ? BooleanFilters :
    T extends SB.NumberSchema ? NumberFilters :
    T extends SB.ObjectSchema<infer P> ? ObjectFilters<P> :
    T extends SB.StringSchema ? StringFilters :
    T extends SB.TupleSchema<infer P> ? StringFilters<P> :
    // Wrapping P in a tuple here prevents union type from getting distributed:
    // without wrapping `SelectBuilder<SB.ArraySchema<'a' | 'b'>>` resolves to `ArrayFilters<'a'> | ArrayFilters<'b'>` and
    // with wrapping to `ArrayFilters<'a' | 'b'>` which is what we want.
    T extends SB.ArraySchema<infer P> ? [P] extends [object] ? ArrayObjectFilters<P>
                                      : ArrayFilters<P> :
    never;

export type SchemaToFilter<T> = T extends MatchableStringSchema
    ? MatchableStringFilters
    : T extends SB.BooleanSchema
    ? BooleanFilters
    : T extends SB.NumberSchema
    ? NumberFilters
    : T extends SB.StringSchema
    ? StringFilters
    : T extends SB.TupleSchema<infer P>
    ? StringFilters<P>
    : T extends SB.ArraySchema<infer P>
    ? [P] extends [boolean] | [number] | [string]
        ? ArrayFilters<P>
        : never
    : never;

export type PrimitiveToFilter<T> = T extends boolean
    ? BooleanFilters
    : T extends number
    ? NumberFilters
    : T extends string
    ? StringExistsFilters<T>
    : T extends (infer P)[]
    ? [P] extends [boolean] | [number] | [string]
        ? ArrayFilters<P>
        : never
    : never;

export type IndexSchema = { [name: string]: ComputedPropertySchema };

// Filters on fields + system filter on `id`.
export type QueryFilter<TIndexSchema extends IndexSchema> = {
    [k in keyof TIndexSchema]?: SelectBuilder<TIndexSchema[k]>;
} & { id?: { isOneOf?: string[]; isNotOneOf?: string[] } }; // system filter on the `id` field:

export type QueryFilters<TIndexSchema extends IndexSchema> =
    | QueryFilter<TIndexSchema>
    | [QueryFilter<TIndexSchema>, ...QueryFilter<TIndexSchema>[]];

export type NumericDecayFunction = {
    function: 'linear' | 'exp' | 'gauss';
    origin: number;
    scale: number;
    offset?: number;
    decay?: number;
};

type NestedDecayFunctionBuilder<T> = T extends SB.NumberSchema
    ? NumericDecayFunction
    : T extends SB.ArraySchema<infer P>
    ? P extends object
        ? P extends any[]
            ? never
            : { [k in keyof P]?: NumericDecayFunction }
        : never
    : never;

export type QueryOpts<TIndexSchema extends IndexSchema> = {
    where: QueryFilters<TIndexSchema>;

    /**
     * Define result sorting order.
     *
     * When the `sort` array includes multiple items, results are first sorted by the first `sort` item,
     * then with the second item in case of ties and so on until the last `sort` item.
     *
     * Cannot be combined with `scoring`.
     */
    sort?:
        | (
              | { field: SortField<TIndexSchema>; order: 'asc' | 'desc' }
              | {
                    /**
                     * A computed property script object created with `createComputedPropertyScripts`.
                     *
                     * `script` sorting can slow down queries especially in case of complex scripts. Note the following to avoid search performance issues:
                     * 1. If possible, use the more performant `field` or `random` sorting instead of `script`.
                     * 2. Whenever resorting to `script` make sure to filter down the search population with `where` clauses as much as possible.
                     *
                     * @see https://docs.dev.gc-internal.net/replicant/basic-features/Indexing#script-based-sorting
                     *
                     * **Only enabled in Replicant online mode**.
                     */
                    script: ComputedPropertyScriptInternal;

                    order: 'asc' | 'desc';
                }
              | 'random'
          )[]
        | 'random';

    /**
     * Only enabled in Replicant online mode. Cannot be combined with `sort`.
     *
     * @see https://docs.dev.gc-internal.net/replicant/basic-features/Indexing#scoring
     */
    scoring?: {
        functions: { [k in keyof TIndexSchema]?: NestedDecayFunctionBuilder<TIndexSchema[k]> };
        should?: {
            [k in keyof TIndexSchema]?: SelectBuilder<TIndexSchema[k]>;
        } & { id?: { isOneOf?: string[]; isNotOneOf?: string[] } };
        mode: 'multiply' | 'sum';
    };
};

export type SearchOpts<TIndexSchema extends IndexSchema> = QueryOpts<TIndexSchema> & {
    /**
     * Select which indexed player properties to include in search results.
     *
     * Defaults to all indexed properties.
     *
     * `id` is always included. Use `fields: []` to only include `id`.
     */
    fields?: string[]; // TODO strict types + reflect in return value

    /** The maximum number of hits to return. Must be an integer between 1 and 10,000. */
    limit: number;
};

// prettier-ignore
/** Union of allowed sort field names, including nested ones like `tournaments.score`. */
type SortField<T extends IndexSchema> = {
    [K in keyof T]:
        // 1) object schemas like `SB.object({ ... })`
        T[K] extends SB.ObjectSchema<infer I> ? `${string & K}.${string & keyof I}` :
        // 2) nested object schemas like `SB.array(SB.object({ ... }))`
        T[K] extends SB.ArraySchema<object & Record<infer J, any>> ? `${string & K}.${string & J}` :
        // 3) any other schema
        K;
}[keyof T & string];

export type QueryResults<TPayloadSchema extends IndexSchema> = {
    results: (SB.ExtractType<SB.ObjectSchema<TPayloadSchema>> & { id: string })[];
};

export type IndexSearchFn<TComputedProperties extends ComputedProperties> = (
    query: SearchOpts<GetIndexSchema<TComputedProperties>>,
    requestOpts?: {
        /**
         * The duration in milliseconds for how long results are calculated in the indexing backend before throwing an error.
         * Note that this only includes result calculation and not things like network requests.
         *
         * @default `2000`
         */
        timeoutMs?: number;
    },
) => Promise<QueryResults<GetPayloadSchema<TComputedProperties>>>;

export type IndexCountFn<TComputedProperties extends ComputedProperties> = (
    query: QueryOpts<GetIndexSchema<TComputedProperties>>,
) => Promise<number>;

export type GetIndexSchema<TComputedProps extends ComputedProperties> = {
    [K in FilterKeys<TComputedProps, { _searchable: true }>]: TComputedProps[K]['type'];
};

export function getIndexSchemaFromComputedProps<TComputedProps extends ComputedProperties>(
    computedProps: TComputedProps,
): GetIndexSchema<TComputedProps> {
    const res: IndexSchema = {};

    for (const key in computedProps) {
        if (computedProps[key]?._searchable) {
            (res as any)[key] = computedProps[key]!.type;
        }
    }

    return res as any;
}

export type GetPayloadSchema<TComputedProps extends ComputedProperties> = {
    [K in FilterKeys<TComputedProps, { _searchable: true } | { _payload: true }>]: TComputedProps[K]['type'];
};

export function getPayloadSchemaFromComputedProps<TComputedProps extends ComputedProperties>(
    computedProps: TComputedProps,
): GetPayloadSchema<TComputedProps> {
    const payloadSchema: { [key: string]: ComputedPropertySchema } = {};

    for (const [key, computed] of Object.entries(computedProps)) {
        if (computed._searchable || computed._payload) {
            payloadSchema[key] = computed.type;
        }
    }

    return payloadSchema as GetPayloadSchema<TComputedProps>;
}
